import React, { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import useSWR from 'swr';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { formatFileSize } from '../../components/attachments/fileIconUtils';
import { formatDateTime } from '../../config/appConfig';
import { cn } from '../../lib/utils';
import { segmentedControlClass, segmentedOptionClass } from '../dashboard/themeHelpers';
import ApplicationDataCard from './components/ApplicationDataCard';
import DetailRow from './components/DetailRow';
import MetricSparkline from './components/MetricSparkline';
import OnlineUsersCard from './components/OnlineUsersCard';
import TodayUserLoginsCard from './components/TodayUserLoginsCard';
import SecurityInfraCard from './components/SecurityInfraCard';
import SyncStatsCard from './components/SyncStatsCard';
import EmailAccountHealthCard from './components/EmailAccountHealthCard';
import HealthStatusHero from './components/HealthStatusHero';
import SectionCard from './components/SectionCard';
import SyncActivityLiveLog from './components/SyncActivityLiveLog';
import SyncLogTimeline from './components/SyncLogTimeline';
import UsageRing from './components/UsageRing';
import type { SystemHealthSnapshot } from './types';
import { formatDuration, usageLevel, systemHealthStatusPath } from './utils';
import {
  infrastructureBandClass,
  infrastructureBandLabelClass,
  resourcesPanelClass,
  zoneLinkClass,
} from './theme';

export type { SystemHealthSnapshot } from './types';

const POLL_INTERVAL_MS = 30_000;

const SystemHealthPage: React.FC = () => {
  const { colors } = useTheme();
  const { can } = useAuth();
  const canManageEmailSync = can('system_health', 'edit');
  const [liveEnabled, setLiveEnabled] = useState(true);

  const { data, error, isLoading, mutate, isValidating } = useSWR<SystemHealthSnapshot>(
    canManageEmailSync ? '/system-health' : null,
    async (url: string) => {
      const res = await api.get(url, { timeout: 60_000 });
      return res.data;
    },
    {
      refreshInterval: liveEnabled ? POLL_INTERVAL_MS : 0,
      refreshWhenHidden: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: liveEnabled,
      // Avoid overlapping polls while the backend is busy (e.g. during email sync).
      dedupingInterval: liveEnabled ? POLL_INTERVAL_MS - 2000 : undefined,
      keepPreviousData: true,
    },
  );

  const collectedLabel = useMemo(() => {
    if (!data?.collectedAt) return null;
    return formatDateTime(data.collectedAt);
  }, [data?.collectedAt]);

  const pausedSyncAccounts = useMemo(() => {
    if (!data?.emailSync.accounts) return [];
    return data.emailSync.accounts.filter((account) => !account.syncEnabled);
  }, [data?.emailSync.accounts]);

  if (!canManageEmailSync) {
    return <Navigate to={systemHealthStatusPath()} replace />;
  }

  if (error) {
    return (
      <div className={cn('p-6', colors.background)}>
        <div className={cn('rounded-2xl border p-6 text-center max-w-lg mx-auto', colors.surface, colors.border)}>
          <div className={cn('inline-flex h-12 w-12 items-center justify-center rounded-full mb-3', colors.error)}>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className={cn('text-lg font-semibold', colors.text)}>Unable to load system health</h2>
          <p className={cn('text-sm mt-2', colors.textMuted)}>
            You may not have permission, or the health endpoint is unavailable on this server.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('w-full flex flex-col gap-6 p-4 md:p-6', colors.background)}>
      <div className={cn('flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-4 p-3 rounded-xl border', colors.surface, colors.border)}>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 flex-1 min-w-0">
          <p className={cn('text-sm', colors.textMuted)}>
            Server health at a glance — disk, memory, uploads, database, and email sync.
          </p>
          {collectedLabel && (
            <span className={cn('text-xs sm:ml-auto tabular-nums', colors.textMuted)}>
              {isValidating && !isLoading ? 'Updating… · ' : ''}
              Last snapshot: {collectedLabel}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-4">
          <div className={segmentedControlClass(colors, true)}>
            <button
              type="button"
              onClick={() => setLiveEnabled(true)}
              className={segmentedOptionClass(colors, liveEnabled, 'soft', true)}
            >
              Live
            </button>
            <button
              type="button"
              onClick={() => setLiveEnabled(false)}
              className={segmentedOptionClass(colors, !liveEnabled, 'soft', true)}
            >
              Paused
            </button>
          </div>
          <button
            type="button"
            onClick={() => mutate()}
            disabled={isValidating}
            className={cn(
              'inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
              colors.primary,
              colors.primaryText,
              isValidating && 'opacity-70',
            )}
          >
            <svg className={cn('w-4 h-4', isValidating && 'animate-spin')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          <Link
            to="/admin/email-logs"
            className={cn('text-sm font-medium px-3 py-1.5 rounded-lg border transition-colors', colors.border, colors.text, colors.interactiveHover)}
          >
            Sync logs
          </Link>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {isLoading && !data ? (
          <div className="space-y-4">
            <div className={cn('h-36 rounded-2xl animate-pulse', colors.surfaceHover)} />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className={cn('h-48 rounded-xl animate-pulse', colors.surfaceHover)} />
              ))}
            </div>
          </div>
        ) : data ? (
          <>
            {pausedSyncAccounts.length > 0 && (
              <div
                className={cn('rounded-xl border px-4 py-3', colors.warning, colors.border)}
                role="status"
              >
                <p className="font-semibold text-sm">
                  {pausedSyncAccounts.length === 1
                    ? '1 email account has sync paused'
                    : `${pausedSyncAccounts.length} email accounts have sync paused`}
                </p>
                <p className="text-sm mt-1">
                  Scheduled sync will not run until you resume each account below. New mail will not be fetched
                  while paused.
                </p>
                <ul className="mt-2 space-y-1 text-sm">
                  {pausedSyncAccounts.map((account) => (
                    <li key={account.id} className="flex gap-2">
                      <span aria-hidden>•</span>
                      <span>
                        {account.displayName ? (
                          <>
                            <span className="font-medium">{account.displayName}</span>
                            <span className={cn('ml-1', colors.textMuted)}>({account.emailAddress})</span>
                          </>
                        ) : (
                          <span className="font-medium">{account.emailAddress}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <HealthStatusHero data={data} />

            {data.warnings.length > 0 && (
              <div className={cn('rounded-xl border px-4 py-3', colors.warning, colors.border)}>
                <p className="font-semibold text-sm mb-2">Active warnings</p>
                <ul className="space-y-1.5 text-sm">
                  {data.warnings.map((w) => (
                    <li key={w} className="flex gap-2">
                      <span aria-hidden>•</span>
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Infrastructure */}
            <div className={infrastructureBandClass()}>
              <p className={infrastructureBandLabelClass()}>Infrastructure</p>

              <SectionCard zone="infrastructure" title="Resources" subtitle="Usage as seen by the backend process on the server">
                {data.trends && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <MetricSparkline
                      label="Disk trend"
                      points={data.trends.disk.map((p) => p.usedPercent)}
                      colorClass="text-emerald-500"
                    />
                    <MetricSparkline
                      label="Memory trend"
                      points={data.trends.memory.map((p) => p.usedPercent)}
                      colorClass="text-indigo-500"
                    />
                    <MetricSparkline
                      label="Uploads size trend"
                      points={data.trends.uploadsBytes
                        .filter((p) => p.bytes != null)
                        .map((p) => Math.round(p.bytes! / (1024 * 1024)))}
                      unit=" MB"
                      colorClass="text-violet-500"
                    />
                  </div>
                )}
                <div className={resourcesPanelClass()}>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                    <UsageRing
                      label="Disk"
                      sublabel={data.disk.root.path}
                      percent={data.disk.root.usedPercent}
                      value={formatFileSize(data.disk.root.availableBytes) + ' free'}
                      detail={`${formatFileSize(data.disk.root.usedBytes)} used of ${formatFileSize(data.disk.root.totalBytes)}`}
                      level={usageLevel(data.disk.root.usedPercent)}
                      size="lg"
                    />
                    <UsageRing
                      label="Memory"
                      percent={data.memory.usedPercent}
                      value={formatFileSize(data.memory.freeBytes) + ' free'}
                      detail={`Process RSS ${formatFileSize(data.memory.processRssBytes)}`}
                      level={usageLevel(data.memory.usedPercent)}
                      size="lg"
                    />
                    <UsageRing
                      label="Uploads"
                      sublabel={data.disk.uploads.path}
                      percent={data.disk.uploads.usedPercent}
                      value={
                        data.disk.uploads.dirSizePending && data.disk.uploads.dirSizeBytes == null
                          ? 'Calculating…'
                          : data.disk.uploads.dirSizeBytes != null
                            ? formatFileSize(data.disk.uploads.dirSizeBytes)
                            : '—'
                      }
                      detail="Attachment folder size on disk"
                      level={usageLevel(data.disk.uploads.usedPercent)}
                      size="lg"
                    />
                    <UsageRing
                      label="Database"
                      percent={0}
                      value={data.database.connected ? 'Online' : 'Offline'}
                      detail={
                        data.database.latencyMs != null
                          ? `${data.database.latencyMs} ms · ${data.database.emailCount?.toLocaleString() ?? '—'} emails`
                          : undefined
                      }
                      level={data.database.connected ? 'ok' : 'error'}
                      size="lg"
                      statusMode
                    />
                  </div>
                </div>
              </SectionCard>

              {data.application && <ApplicationDataCard data={data} />}

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <SectionCard zone="infrastructure" title="Server & process">
                  <DetailRow label="Hostname" value={data.server.hostname} mono />
                  <DetailRow label="Platform" value={data.server.platform} />
                  <DetailRow label="Environment" value={data.server.environment} />
                  <DetailRow label="Node.js" value={data.server.nodeVersion} mono />
                  <DetailRow label="Uptime" value={formatDuration(data.server.processUptimeSeconds)} />
                  <DetailRow label="Process RSS" value={formatFileSize(data.memory.processRssBytes)} />
                  <DetailRow label="Node heap used" value={formatFileSize(data.memory.heapUsedBytes)} />
                  <DetailRow
                    label="CPU cores"
                    value={data.application ? String(data.application.cpuCount) : '—'}
                  />
                  <DetailRow
                    label="Load average (1m)"
                    value={
                      data.application?.loadAvg1m != null ? String(data.application.loadAvg1m) : '—'
                    }
                  />
                </SectionCard>
                <SecurityInfraCard security={data.security} postgres={data.postgres} />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
              {data.onlineUsers && <OnlineUsersCard onlineUsers={data.onlineUsers} />}
              {data.syncStats && <SyncStatsCard syncStats={data.syncStats} />}
            </div>

            {data.todayUserSessions && (
              <TodayUserLoginsCard initialTodaySessions={data.todayUserSessions} />
            )}

            <SectionCard
              zone="mail"
              title="Email sync accounts"
              subtitle="Per-account sync health and last activity"
              action={
                <Link to="/email-accounts" className={zoneLinkClass('mail')}>
                  Manage accounts
                </Link>
              }
            >
              {data.emailSync.accounts.length === 0 ? (
                <p className={cn('text-sm py-6 text-center', colors.textMuted)}>No email accounts configured.</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {data.emailSync.accounts.map((account) => (
                    <EmailAccountHealthCard
                      key={account.id}
                      account={account}
                      canManageSync={canManageEmailSync}
                      onSyncPaused={async () => { await mutate(); }}
                      onSyncResumed={async () => { await mutate(); }}
                    />
                  ))}
                </div>
              )}
            </SectionCard>

            <SyncActivityLiveLog />

            <SectionCard
              zone="mail"
              title="Recent sync activity"
              subtitle="Latest email sync runs across all accounts"
              action={
                <Link to="/admin/email-logs" className={zoneLinkClass('mail')}>
                  View all logs →
                </Link>
              }
            >
              <SyncLogTimeline logs={data.emailSync.recentLogs} />
            </SectionCard>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default SystemHealthPage;
